const CUSTOMER_DISPLAY_CHANNEL_NAME = 'customer_display'
const CUSTOMER_DISPLAY_TAB_ID_STORAGE_KEY = 'customerDisplayTabId'
export const CUSTOMER_DISPLAY_OWNER_TAB_ID_STORAGE_KEY =
  'customerDisplayOwnerTabId'
export const CUSTOMER_DISPLAY_OWNER_TAB_STATUS_STORAGE_KEY =
  'customerDisplayOwnerTabStatus'
export const CUSTOMER_DISPLAY_MAIN_TAB_CLOSING_STORAGE_KEY =
  'customerDisplayMainTabClosing'
const CUSTOMER_DISPLAY_MESSAGE_SOURCE = 'customerDisplay'

const createFallbackId = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2)}`

let currentCustomerDisplayTabId = null

const shouldReuseStoredTabId = () => {
  const navigationEntries =
    globalThis.performance?.getEntriesByType?.('navigation')
  const navigationEntry = navigationEntries?.[0]
  const navigationType = navigationEntry?.type

  return !navigationType || ['reload', 'back_forward'].includes(navigationType)
}

export const getCustomerDisplayTabId = () => {
  if (currentCustomerDisplayTabId) return currentCustomerDisplayTabId

  const storedTabId = globalThis.sessionStorage?.getItem(
    CUSTOMER_DISPLAY_TAB_ID_STORAGE_KEY
  )

  if (storedTabId && shouldReuseStoredTabId()) {
    currentCustomerDisplayTabId = storedTabId
    return currentCustomerDisplayTabId
  }

  currentCustomerDisplayTabId =
    globalThis.crypto?.randomUUID?.() || createFallbackId()
  globalThis.sessionStorage?.setItem(
    CUSTOMER_DISPLAY_TAB_ID_STORAGE_KEY,
    currentCustomerDisplayTabId
  )

  return currentCustomerDisplayTabId
}

export const createCustomerDisplayChannel = () => {
  if (typeof BroadcastChannel === 'undefined') return null

  return new BroadcastChannel(CUSTOMER_DISPLAY_CHANNEL_NAME)
}

export const getCustomerDisplayOwnerTabId = () =>
  globalThis.localStorage?.getItem(CUSTOMER_DISPLAY_OWNER_TAB_ID_STORAGE_KEY) ||
  null

const getCurrentDocumentHidden = () =>
  typeof globalThis.document !== 'undefined'
    ? Boolean(globalThis.document.hidden)
    : false

export const parseCustomerDisplayOwnerTabStatus = value => {
  if (!value) return null

  try {
    const data = JSON.parse(value)
    return data?.tabId ? data : null
  } catch (error) {
    console.warn(
      'No se pudo leer el estado de la pestaña dueña del Customer Display:',
      error
    )
    return null
  }
}

export const getCustomerDisplayOwnerTabStatus = () =>
  parseCustomerDisplayOwnerTabStatus(
    globalThis.localStorage?.getItem(
      CUSTOMER_DISPLAY_OWNER_TAB_STATUS_STORAGE_KEY
    )
  )

export const setCustomerDisplayOwnerTabStatus = ({
  tabId = getCustomerDisplayTabId(),
  isHidden = getCurrentDocumentHidden(),
  isClosing = false,
  isConnected = false,
} = {}) => {
  if (!globalThis.localStorage || !tabId) return null

  const status = {
    tabId,
    isHidden: Boolean(isHidden),
    isClosing: Boolean(isClosing),
    isConnected: Boolean(isConnected),
    updatedAt: Date.now(),
  }

  try {
    globalThis.localStorage.setItem(
      CUSTOMER_DISPLAY_OWNER_TAB_STATUS_STORAGE_KEY,
      JSON.stringify(status)
    )
  } catch (error) {
    console.warn(
      'No se pudo guardar el estado de la pestaña dueña del Customer Display:',
      error
    )
  }

  return status
}

const clearCustomerDisplayOwnerTabStatus = tabId => {
  const currentStatus = getCustomerDisplayOwnerTabStatus()

  if (tabId && currentStatus?.tabId === tabId) {
    globalThis.localStorage?.removeItem(
      CUSTOMER_DISPLAY_OWNER_TAB_STATUS_STORAGE_KEY
    )
  }
}

export const setCustomerDisplayOwnerTabId = (
  tabId = getCustomerDisplayTabId(),
  ownerStatus = {}
) => {
  globalThis.localStorage?.setItem(
    CUSTOMER_DISPLAY_OWNER_TAB_ID_STORAGE_KEY,
    tabId
  )
  setCustomerDisplayOwnerTabStatus({ ...ownerStatus, tabId })

  return tabId
}

export const clearCustomerDisplayOwnerTabId = tabId => {
  const currentOwnerTabId = getCustomerDisplayOwnerTabId()

  if (!tabId || currentOwnerTabId !== tabId) return false

  globalThis.localStorage?.removeItem(CUSTOMER_DISPLAY_OWNER_TAB_ID_STORAGE_KEY)
  clearCustomerDisplayOwnerTabStatus(tabId)

  return true
}

export const notifyCustomerDisplayMainTabClosing = (
  tabId = getCustomerDisplayTabId()
) => {
  if (!globalThis.localStorage || !tabId) return

  try {
    globalThis.localStorage.setItem(
      CUSTOMER_DISPLAY_MAIN_TAB_CLOSING_STORAGE_KEY,
      JSON.stringify({
        tabId,
        timestamp: Date.now(),
        eventId: createFallbackId(),
      })
    )
  } catch (error) {
    console.warn(
      'No se pudo notificar el cierre de la pestaña principal del Customer Display:',
      error
    )
  }
}

export const parseCustomerDisplayMainTabClosingEvent = value => {
  try {
    const data = JSON.parse(value)
    return data?.tabId ? data : null
  } catch (error) {
    console.warn(
      'No se pudo leer el evento de cierre de la pestaña principal del Customer Display:',
      error
    )
    return null
  }
}

export const postCustomerDisplayMainMessage = (channel, message) => {
  channel?.postMessage({
    ...message,
    tabId: getCustomerDisplayTabId(),
  })
}

export const postCustomerDisplayTargetedMessage = (
  channel,
  message,
  targetTabId
) => {
  channel?.postMessage({
    ...message,
    source: CUSTOMER_DISPLAY_MESSAGE_SOURCE,
    ...(targetTabId && { targetTabId }),
  })
}

export const isCustomerDisplayMessageForCurrentTab = data =>
  !data?.targetTabId || data.targetTabId === getCustomerDisplayTabId()

export const isCustomerDisplaySourceMessage = data =>
  data?.source === CUSTOMER_DISPLAY_MESSAGE_SOURCE

export const getCustomerDisplayMessageOwnerTabId = data =>
  data?.ownerTabId || data?.tabId || null
